/* eslint-disable no-unused-vars */
import { useMemo, useState, memo } from 'react';
import {
    Box,
    Flex,
    Grid,
    Heading,
    Text,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Skeleton,
    SimpleGrid,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { addCommasToNumber } from 'utils/utils';
import useWallet, { TRANSACTION_STATUSES } from 'hooks/useWallet';
import useWithdrawalFlow from 'hooks/useWithdrawalFlow';
import useDebounce from 'hooks/useDebounce';
import useGetUserDetails from 'hooks/useGetUserDetail';
import WalletModals from 'components/Wallet/WalletModals';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SUCCESS     = '#10B981';
const SUCCESS_BG  = '#ECFDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';
const WARNING     = '#EA580C';
const WARNING_BG  = '#FFF7ED';
const INFO        = '#2563EB';
const INFO_BG     = '#EFF6FF';
const PURPLE      = '#7C3AED';
const PURPLE_BG   = '#EDE9FE';

const FONT = "'Bricolage Grotesque', sans-serif";

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

const ACCENT_COLORS = {
    navy:    { color: NAVY,    bg: CYAN       },
    success: { color: SUCCESS, bg: SUCCESS_BG },
    danger:  { color: DANGER,  bg: DANGER_BG  },
    warning: { color: WARNING, bg: WARNING_BG },
    info:    { color: INFO,    bg: INFO_BG    },
    purple:  { color: PURPLE,  bg: PURPLE_BG  },
};

const SEARCH_DEBOUNCE_MS = 400;

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────── */

const formatDate = (ds) => {
    if (!ds) return '—';
    try {
        return new Date(ds).toLocaleDateString('en-NG', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    } catch { return '—'; }
};

const formatTime = (ds) => {
    if (!ds) return '';
    try {
        return new Date(ds).toLocaleTimeString('en-NG', {
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return ''; }
};

const maskAccountNumber = (acct) => {
    if (!acct) return null;
    const s = String(acct);
    if (s.length < 4) return s;
    return `•••• ${s.slice(-4)}`;
};

/* ─────────────────────────────────────────────────────────────
   ICONS (inline SVG)
   ───────────────────────────────────────────────────────────── */

const SearchIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const RefreshIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0114.85-3.36L23 10M1 14l4.65 4.36A9 9 0 0020.5 15"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const ArrowUpRightIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M7 17L17 7M7 7h10v10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronDownIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InboxIcon = (props) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 5h13l3 7v6a2 2 0 01-2 2h-15a2 2 0 01-2-2v-6l3-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   STATUS BADGE
   ───────────────────────────────────────────────────────────── */

const TX_STATUS_META = {
    paid:     { label: 'Completed', color: SUCCESS, bg: SUCCESS_BG },
    pending:  { label: 'Pending',   color: WARNING, bg: WARNING_BG },
    reversed: { label: 'Declined',  color: DANGER,  bg: DANGER_BG  },
};

const StatusBadge = memo(function StatusBadge({ status }) {
    const meta = TX_STATUS_META[(status ?? '').toLowerCase()]
        || { label: status || 'Unknown', color: MUTED, bg: SURFACE_ALT };
    return (
        <Box
            display="inline-block"
            bg={meta.bg}
            color={meta.color}
            fontSize="9.5px"
            fontWeight="800"
            px="8px"
            py="2px"
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="0.04em"
            border="1px solid"
            borderColor={`${meta.color}30`}
            whiteSpace="nowrap"
        >
            {meta.label}
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   WALLET HEADER CARD — balance + withdraw button + account
   ───────────────────────────────────────────────────────────── */

const WalletHeaderCard = memo(function WalletHeaderCard({
    wallet, currencySymbol, onWithdraw, accountNumberMasked,
}) {
    return (
        <Box
            bg={NAVY}
            color="white"
            borderRadius="16px"
            p={{ base: '20px', md: '28px' }}
            mb="20px"
            position="relative"
            overflow="hidden"
            fontFamily={FONT}
        >
            {/* Decorative gradient blob */}
            <Box
                position="absolute"
                top="-40px"
                right="-40px"
                w="200px"
                h="200px"
                borderRadius="full"
                bg={CYAN}
                opacity={0.08}
            />

            <Flex
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                gap="16px"
                position="relative"
            >
                <Box>
                    <Text fontSize="10px" fontWeight="700" color="whiteAlpha.700" letterSpacing="0.1em" mb="6px">
                        AVAILABLE FOR PAYOUT
                    </Text>
                    <Heading
                        as="h1"
                        fontSize={{ base: '28px', md: '36px' }}
                        fontWeight="800"
                        fontFamily={FONT}
                        letterSpacing="-0.02em"
                        lineHeight="1"
                        mb="8px"
                    >
                        {currencySymbol}{addCommasToNumber(Number(wallet?.ledger_balance ?? 0).toFixed(2))}
                    </Heading>
                    <Flex gap="4px" align="center" fontSize="12px" color="whiteAlpha.800">
                        <Text>Total balance:</Text>
                        <Text fontWeight="700" color="white">
                            {currencySymbol}{addCommasToNumber(Number(wallet?.actual_balance ?? 0).toFixed(2))}
                        </Text>
                    </Flex>
                    {accountNumberMasked && (
                        <Text fontSize="11px" color="whiteAlpha.700" mt="6px" fontFamily="monospace">
                            Account {accountNumberMasked}
                        </Text>
                    )}
                </Box>

                <Button
                    onClick={onWithdraw}
                    bg="white"
                    color={NAVY}
                    _hover={{ bg: CYAN }}
                    _active={{ bg: CYAN }}
                    h="46px"
                    px="22px"
                    fontSize="13px"
                    fontWeight="800"
                    fontFamily={FONT}
                    borderRadius="10px"
                    rightIcon={<ArrowUpRightIcon />}
                    boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                >
                    Withdraw
                </Button>
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   STAT CARD
   ───────────────────────────────────────────────────────────── */

const StatCard = memo(function StatCard({ card, currencySymbol }) {
    const accent = ACCENT_COLORS[card.accent] ?? ACCENT_COLORS.navy;

    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            p="16px"
            boxShadow={SHADOW_SM}
            position="relative"
            overflow="hidden"
        >
            <Box position="absolute" left="0" top="0" bottom="0" w="3px" bg={accent.color} />
            <Text
                fontSize="9.5px"
                fontWeight="800"
                color={FAINT}
                letterSpacing="0.08em"
                textTransform="uppercase"
                mb="10px"
                fontFamily={FONT}
            >
                {card.name}
            </Text>
            <Flex justify="space-between" align="baseline" mb="6px">
                <Text fontSize="10.5px" color={MUTED} fontWeight="600">
                    {card.primaryLabel}
                </Text>
                <Text fontSize="14px" fontWeight="800" color={INK} fontFamily={FONT}>
                    {currencySymbol}{addCommasToNumber(Number(card.primaryValue ?? 0).toFixed(2))}
                </Text>
            </Flex>
            <Flex justify="space-between" align="baseline">
                <Text fontSize="10px" color={FAINT}>
                    {card.secondaryLabel}
                </Text>
                <Text fontSize="11px" fontWeight="600" color={MUTED}>
                    {currencySymbol}{addCommasToNumber(Number(card.secondaryValue ?? 0).toFixed(2))}
                </Text>
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   STATUS DROPDOWN
   ───────────────────────────────────────────────────────────── */

const STATUS_DOT_COLORS = {
    all:      FAINT,
    pending:  WARNING,
    reversed: DANGER,
    paid:     SUCCESS,
};

const StatusDropdown = memo(function StatusDropdown({ value, onChange }) {
    const selected = TRANSACTION_STATUSES.find((s) => s.value === value) ?? TRANSACTION_STATUSES[0];

    return (
        <Menu placement="bottom-start" matchWidth autoSelect={false}>
            {({ isOpen }) => (
                <>
                    <MenuButton
                        as={Button}
                        rightIcon={
                            <Box
                                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                transition="transform 0.18s ease"
                                color={FAINT}
                            >
                                <ChevronDownIcon />
                            </Box>
                        }
                        w="full"
                        h="36px"
                        px="12px"
                        bg={SURFACE_ALT}
                        border="1px solid"
                        borderColor={isOpen ? NAVY : BORDER}
                        boxShadow={isOpen ? `0 0 0 3px ${CYAN}` : 'none'}
                        borderRadius="8px"
                        textAlign="left"
                        fontFamily={FONT}
                        _hover={{ borderColor: isOpen ? NAVY : '#CBD5E0', bg: SURFACE_ALT }}
                        _active={{ bg: SURFACE_ALT }}
                        sx={{ '& > span': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, minWidth: 0 } }}
                    >
                        <Flex align="center" gap="8px" minW="0">
                            <Box w="8px" h="8px" borderRadius="full" bg={STATUS_DOT_COLORS[selected.id]} flexShrink={0} />
                            <Text fontSize="12px" color={INK} fontWeight="600" noOfLines={1}>
                                {selected.label}
                            </Text>
                        </Flex>
                    </MenuButton>
                    <MenuList
                        p="6px"
                        borderRadius="10px"
                        border="1px solid"
                        borderColor={BORDER}
                        boxShadow={SHADOW_SM}
                        bg={SURFACE}
                        fontFamily={FONT}
                        minW="0"
                        zIndex={20}
                    >
                        {TRANSACTION_STATUSES.map((s) => {
                            const isSelected = s.value === value;
                            return (
                                <MenuItem
                                    key={s.id}
                                    onClick={() => onChange(s.value)}
                                    bg={isSelected ? CYAN : 'transparent'}
                                    color={isSelected ? NAVY : MUTED}
                                    fontSize="12px"
                                    fontWeight={isSelected ? '700' : '500'}
                                    borderRadius="6px"
                                    mb="2px"
                                    px="10px"
                                    py="8px"
                                    _hover={{ bg: isSelected ? CYAN : SURFACE_ALT, color: NAVY }}
                                    _focus={{ bg: isSelected ? CYAN : SURFACE_ALT, color: NAVY }}
                                >
                                    <Flex align="center" justify="space-between" w="full" gap="8px">
                                        <Flex align="center" gap="8px">
                                            <Box w="8px" h="8px" borderRadius="full" bg={STATUS_DOT_COLORS[s.id]} />
                                            <Text as="span">{s.label}</Text>
                                        </Flex>
                                        {isSelected && <Box color={NAVY}><CheckIcon /></Box>}
                                    </Flex>
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </>
            )}
        </Menu>
    );
});

/* ─────────────────────────────────────────────────────────────
   ACTIVE FILTER PILL
   ───────────────────────────────────────────────────────────── */

const ActiveFilterPill = ({ label, value, onClear }) => (
    <Flex
        align="center"
        gap="6px"
        bg={CYAN}
        color={NAVY}
        fontSize="11px"
        fontWeight="700"
        fontFamily={FONT}
        px="10px"
        py="4px"
        borderRadius="full"
        border="1px solid"
        borderColor={`${NAVY}20`}
    >
        <Text as="span" color={MUTED} fontWeight="600">{label}:</Text>
        <Text as="span" noOfLines={1} maxW="140px">{value}</Text>
        <IconButton
            aria-label={`Clear ${label}`}
            icon={<CloseIcon />}
            onClick={onClear}
            size="xs"
            h="16px"
            minW="16px"
            w="16px"
            borderRadius="full"
            bg="transparent"
            color={NAVY}
            _hover={{ bg: 'whiteAlpha.700' }}
        />
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   TRANSACTION ROW (desktop)
   ───────────────────────────────────────────────────────────── */

const TransactionRow = memo(function TransactionRow({ tx, currencySymbol, isLast }) {
    const isOutbound = tx.type?.toLowerCase().includes('debit')
        || tx.type?.toLowerCase().includes('withdraw');

    return (
        <Tr borderBottom={isLast ? 'none' : '1px solid'} borderColor={BORDER} _hover={{ bg: SURFACE_ALT }}>
            <Td py="12px" borderColor={BORDER}>
                <Text fontSize="12px" color={INK} fontWeight="600">{formatDate(tx.createdAt)}</Text>
                <Text fontSize="10px" color={FAINT}>{formatTime(tx.createdAt)}</Text>
            </Td>
            <Td py="12px" borderColor={BORDER}>
                <Flex align="center" gap="8px">
                    <StatusBadge status={tx.status} />
                </Flex>
                <Text
                    as="code"
                    fontSize="11px"
                    color={MUTED}
                    fontFamily="monospace"
                    mt="2px"
                    display="block"
                >
                    {tx.transaction_id ?? '—'}
                </Text>
            </Td>
            <Td py="12px" borderColor={BORDER}>
                <Text fontSize="12px" color={INK} fontWeight="600" textTransform="capitalize">
                    {tx.type ?? '—'}
                </Text>
            </Td>
            <Td py="12px" borderColor={BORDER}>
                <Text fontSize="12px" color={MUTED}>
                    {currencySymbol} {addCommasToNumber(Number(tx.opening_balance ?? 0).toFixed(2))}
                </Text>
            </Td>
            <Td py="12px" borderColor={BORDER}>
                <Text fontSize="12px" fontWeight="800" color={isOutbound ? DANGER : SUCCESS}>
                    {isOutbound ? '−' : '+'}{currencySymbol} {addCommasToNumber(Number(tx.amount ?? 0).toFixed(2))}
                </Text>
            </Td>
            <Td py="12px" borderColor={BORDER}>
                <Text fontSize="12px" color={INK} fontWeight="600">
                    {currencySymbol} {addCommasToNumber(Number(tx.closing_balance ?? 0).toFixed(2))}
                </Text>
            </Td>
            <Td py="12px" borderColor={BORDER}>
                <Text fontSize="11px" color={MUTED} fontFamily="monospace">
                    {tx.order_number ?? '—'}
                </Text>
            </Td>
        </Tr>
    );
});

/* ─────────────────────────────────────────────────────────────
   TRANSACTION CARD (mobile)
   ───────────────────────────────────────────────────────────── */

const TransactionCard = memo(function TransactionCard({ tx, currencySymbol }) {
    const isOutbound = tx.type?.toLowerCase().includes('debit')
        || tx.type?.toLowerCase().includes('withdraw');

    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            p="14px"
            boxShadow={SHADOW_SM}
        >
            <Flex justify="space-between" align="flex-start" mb="10px" gap="8px">
                <Box flex="1" minW="0">
                    <Text fontSize="12px" fontWeight="700" color={INK} textTransform="capitalize" noOfLines={1}>
                        {tx.type ?? 'Transaction'}
                    </Text>
                    <Text
                        as="code"
                        fontSize="10px"
                        color={MUTED}
                        fontFamily="monospace"
                        noOfLines={1}
                        mt="2px"
                    >
                        {tx.transaction_id ?? '—'}
                    </Text>
                </Box>
                <Box textAlign="right">
                    <Text fontSize="14px" fontWeight="800" color={isOutbound ? DANGER : SUCCESS}>
                        {isOutbound ? '−' : '+'}{currencySymbol}{addCommasToNumber(Number(tx.amount ?? 0).toFixed(2))}
                    </Text>
                    <Text fontSize="10px" color={FAINT} mt="2px">
                        {formatDate(tx.createdAt)}
                    </Text>
                </Box>
            </Flex>

            <Flex gap="6px" mb="10px">
                <StatusBadge status={tx.status} />
            </Flex>

            <Grid templateColumns="1fr 1fr" gap="6px" pt="10px" borderTop="1px solid" borderColor={BORDER}>
                <Box>
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.06em">
                        OPENING
                    </Text>
                    <Text fontSize="11px" fontWeight="600" color={MUTED}>
                        {currencySymbol}{addCommasToNumber(Number(tx.opening_balance ?? 0).toFixed(2))}
                    </Text>
                </Box>
                <Box textAlign="right">
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.06em">
                        CLOSING
                    </Text>
                    <Text fontSize="11px" fontWeight="700" color={INK}>
                        {currencySymbol}{addCommasToNumber(Number(tx.closing_balance ?? 0).toFixed(2))}
                    </Text>
                </Box>
            </Grid>

            {tx.order_number && (
                <Text fontSize="10px" color={FAINT} mt="8px" fontFamily="monospace">
                    Order: {tx.order_number}
                </Text>
            )}
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   SKELETONS
   ───────────────────────────────────────────────────────────── */

const TableSkeleton = ({ count = 5 }) => (
    <Box bg={SURFACE} border="1px solid" borderColor={BORDER} borderRadius="12px" overflow="hidden">
        {Array.from({ length: count }).map((_, i) => (
            <Flex
                key={i}
                p="14px"
                gap="14px"
                borderBottom={i === count - 1 ? 'none' : '1px solid'}
                borderColor={BORDER}
            >
                <Skeleton w="80px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="100px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton flex="1" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="80px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="100px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        ))}
    </Box>
);

const CardSkeleton = ({ count = 3 }) => (
    <Flex direction="column" gap="10px">
        {Array.from({ length: count }).map((_, i) => (
            <Skeleton key={i} h="120px" borderRadius="12px" startColor={SURFACE_ALT} endColor={BORDER} />
        ))}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */

const EmptyState = ({ hasFilters, onClearAll }) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        py="60px"
        px="20px"
        textAlign="center"
        gap="12px"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="12px"
        boxShadow={SHADOW_SM}
    >
        <Flex w="60px" h="60px" borderRadius="full" bg={SURFACE_ALT} color={FAINT} align="center" justify="center">
            <InboxIcon />
        </Flex>
        <Box>
            <Heading as="h3" fontSize="15px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                {hasFilters ? 'No transactions match your filters' : 'No transactions yet'}
            </Heading>
            <Text fontSize="12px" color={MUTED}>
                {hasFilters
                    ? 'Try adjusting your filters to see more results.'
                    : 'Your wallet activity will appear here.'}
            </Text>
        </Box>
        {hasFilters && (
            <Button
                onClick={onClearAll}
                size="sm"
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                fontSize="12px"
                fontWeight="700"
                borderRadius="8px"
                h="36px"
                px="14px"
            >
                Clear all filters
            </Button>
        )}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

const Wallet = () => {
    const walletData = useWallet();
    const {
        wallet,
        statCards,
        history,
        totalHistoryCount,
        loading,
        currencySymbol,
        statusFilter, setStatusFilter,
        referenceFilter, setReferenceFilter,
        dateFilter, setDateFilter,
        activeFilters, hasActiveFilters,
        clearFilter, clearAllFilters,
        refresh,
    } = walletData;

    const flow = useWithdrawalFlow({ wallet, currencySymbol, onComplete: refresh });

    /* Debounced reference search */
    const [localRef, setLocalRef] = useState(referenceFilter);
    const debouncedRef = useDebounce(localRef, SEARCH_DEBOUNCE_MS);
    useMemo(() => { setReferenceFilter(debouncedRef); }, [debouncedRef, setReferenceFilter]);

    /* Masked account number */
    const { flattenedUser } = useGetUserDetails();
    const accountNumberMasked = useMemo(() => {
        const acct = flattenedUser?.attributes?.account_number
            ?? flattenedUser?.account_number;
        return maskAccountNumber(acct);
    }, [flattenedUser]);

    const hasResults = history.length > 0;
    const showSkeleton = loading && totalHistoryCount === 0;

    return (
        <Box bg={SURFACE_ALT} minH="100vh" p={{ base: '16px', md: '24px' }} fontFamily={FONT}>
            <Box maxW="1400px" mx="auto">
                {/* ── PAGE HEADER ── */}
                <Flex justify="space-between" align="center" mb="16px">
                    <Heading
                        as="h1"
                        fontSize={{ base: '22px', md: '26px' }}
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                        letterSpacing="-0.02em"
                    >
                        Wallet
                    </Heading>

                    <Button
                        onClick={refresh}
                        isLoading={loading}
                        variant="ghost"
                        color={NAVY}
                        size="sm"
                        h="34px"
                        px="12px"
                        fontSize="12px"
                        fontWeight="700"
                        fontFamily={FONT}
                        borderRadius="8px"
                        leftIcon={<RefreshIcon />}
                        _hover={{ bg: SURFACE_ALT }}
                    >
                        Refresh
                    </Button>
                </Flex>

                {/* ── BALANCE HEADER CARD ── */}
                <WalletHeaderCard
                    wallet={wallet}
                    currencySymbol={currencySymbol}
                    onWithdraw={flow.startWithdrawal}
                    accountNumberMasked={accountNumberMasked}
                />

                {/* ── STAT CARDS ── */}
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="10px" mb="20px">
                    {statCards.map((card) => (
                        <StatCard key={card.id} card={card} currencySymbol={currencySymbol} />
                    ))}
                </SimpleGrid>

                {/* ── TRANSACTIONS HEADER ── */}
                <Flex justify="space-between" align="center" mb="12px" gap="10px" flexWrap="wrap">
                    <Heading
                        as="h2"
                        fontSize={{ base: '16px', md: '18px' }}
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                    >
                        Transactions
                    </Heading>
                    <Text fontSize="11px" color={MUTED}>
                        {totalHistoryCount.toLocaleString('en-NG')} total
                    </Text>
                </Flex>

                {/* ── FILTER BAR ── */}
                <Box
                    bg={SURFACE}
                    border="1px solid"
                    borderColor={BORDER}
                    borderRadius="12px"
                    p="12px"
                    mb="14px"
                    boxShadow={SHADOW_SM}
                >
                    <Grid
                        templateColumns={{ base: '1fr', sm: '2fr 1fr 1fr' }}
                        gap="10px"
                    >
                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                SEARCH
                            </Text>
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none" h="36px">
                                    <Box color={FAINT}><SearchIcon /></Box>
                                </InputLeftElement>
                                <Input
                                    type="search"
                                    value={localRef}
                                    onChange={(e) => setLocalRef(e.target.value)}
                                    placeholder="Transaction ID or order number"
                                    h="36px"
                                    pl="32px"
                                    fontSize="12px"
                                    bg={SURFACE_ALT}
                                    border="1px solid"
                                    borderColor={BORDER}
                                    borderRadius="8px"
                                    fontFamily={FONT}
                                    _placeholder={{ color: FAINT }}
                                    _hover={{ borderColor: '#CBD5E0' }}
                                    _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                                />
                            </InputGroup>
                        </Box>

                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                STATUS
                            </Text>
                            <StatusDropdown value={statusFilter === 'all' ? '' : statusFilter} onChange={(v) => setStatusFilter(v || 'all')} />
                        </Box>

                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                DATE
                            </Text>
                            <Input
                                type="date"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                size="sm"
                                h="36px"
                                fontSize="12px"
                                bg={SURFACE_ALT}
                                borderColor={BORDER}
                                fontFamily={FONT}
                                borderRadius="8px"
                                _hover={{ borderColor: '#CBD5E0' }}
                                _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                            />
                        </Box>
                    </Grid>
                </Box>

                {/* ── ACTIVE FILTER PILLS ── */}
                {hasActiveFilters && (
                    <Flex align="center" gap="8px" flexWrap="wrap" mb="14px">
                        <Text fontSize="11px" color={FAINT} fontWeight="700" letterSpacing="0.08em">
                            ACTIVE:
                        </Text>
                        {activeFilters.map((f) => (
                            <ActiveFilterPill
                                key={f.key}
                                label={f.label}
                                value={f.value}
                                onClear={() => {
                                    if (f.key === 'reference') setLocalRef('');
                                    clearFilter(f.key);
                                }}
                            />
                        ))}
                        <Button
                            onClick={() => { setLocalRef(''); clearAllFilters(); }}
                            size="xs"
                            variant="ghost"
                            color={MUTED}
                            fontSize="11px"
                            fontWeight="700"
                            _hover={{ color: NAVY, bg: 'transparent', textDecoration: 'underline' }}
                            h="24px"
                            px="6px"
                        >
                            Clear all
                        </Button>
                    </Flex>
                )}

                {/* ── BODY ── */}
                {showSkeleton ? (
                    <>
                        <Box display={{ base: 'none', lg: 'block' }}>
                            <TableSkeleton count={6} />
                        </Box>
                        <Box display={{ base: 'block', lg: 'none' }}>
                            <CardSkeleton count={4} />
                        </Box>
                    </>
                ) : !hasResults ? (
                    <EmptyState
                        hasFilters={hasActiveFilters}
                        onClearAll={() => { setLocalRef(''); clearAllFilters(); }}
                    />
                ) : (
                    <Box opacity={loading ? 0.6 : 1} transition="opacity 0.2s ease">
                        {/* Desktop table */}
                        <Box display={{ base: 'none', lg: 'block' }}>
                            <Box
                                bg={SURFACE}
                                border="1px solid"
                                borderColor={BORDER}
                                borderRadius="12px"
                                overflow="hidden"
                                boxShadow={SHADOW_SM}
                            >
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead bg={SURFACE_ALT}>
                                            <Tr>
                                                {['Date', 'Status / ID', 'Type', 'Opening', 'Amount', 'Closing', 'Order'].map((h) => (
                                                    <Th
                                                        key={h}
                                                        py="12px"
                                                        fontSize="10px"
                                                        fontWeight="800"
                                                        color={MUTED}
                                                        letterSpacing="0.08em"
                                                        textTransform="uppercase"
                                                        borderColor={BORDER}
                                                    >
                                                        {h}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {history.map((tx, i) => (
                                                <TransactionRow
                                                    key={tx.id ?? i}
                                                    tx={tx}
                                                    currencySymbol={currencySymbol}
                                                    isLast={i === history.length - 1}
                                                />
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>

                        {/* Mobile cards */}
                        <Flex display={{ base: 'flex', lg: 'none' }} direction="column" gap="10px">
                            {history.map((tx, i) => (
                                <TransactionCard
                                    key={tx.id ?? i}
                                    tx={tx}
                                    currencySymbol={currencySymbol}
                                />
                            ))}
                        </Flex>

                        <Flex justify="center" mt="16px">
                            <Text fontSize="11px" color={MUTED} fontWeight="600">
                                Showing {history.length.toLocaleString('en-NG')} of {totalHistoryCount.toLocaleString('en-NG')} transactions
                            </Text>
                        </Flex>
                    </Box>
                )}
            </Box>

            {/* ── WITHDRAWAL MODALS ── */}
            <WalletModals
                flow={flow}
                wallet={wallet}
                currencySymbol={currencySymbol}
            />
        </Box>
    );
};

export default Wallet;